import {ElementUtil} from '../../utils/elementUtil.js';
import {TimeUtil} from '../../utils/timeUtil.js';
import {appendMigrationRunStepSummaryLines} from './migrationStepOptions.js';

/**
 * Shared option fields for New / Edit / Re-Sync migration forms.
 *
 * options.includePullAll (default true): date range + pull-history checkbox
 * options.includePrimaryOptions (default true): cleanup / lookback / archive —
 *   primary-migration settings. Re-sync forms should set this false; the server
 *   inherits those values from the primary record when omitted.
 * options.includeReplaceExisting (default false): re-sync checkbox to overwrite
 *   existing GPS, fuel, and trip rows matched by V6 id in the date window.
 */
export function buildMigrationOptionFields(options) {
	options = options || {};
	var includePullAll = options.includePullAll !== false;
	var includePrimaryOptions = options.includePrimaryOptions !== false;
	var includeReplaceExisting = options.includeReplaceExisting === true;
	var dataStartPicker;
	var dataEndPicker;
	var pullAllCheckbox;

	function updateDateFieldsEnabled(pullAllData) {
		if (dataStartPicker) {
			dataStartPicker.disabled = !!pullAllData;
		}
		if (dataEndPicker) {
			dataEndPicker.disabled = !!pullAllData;
		}
	}

	var fields = [];

	if (includePullAll) {
		fields.push({
			tag: 'vaadin-date-picker',
			name: 'dataStartDate',
			label: 'Data Start',
			postinit: function(picker) {
				dataStartPicker = picker;
			}
		});
		fields.push({
			tag: 'vaadin-date-picker',
			name: 'dataEndDate',
			label: 'Data End',
			postinit: function(picker) {
				dataEndPicker = picker;
			}
		});
		fields.push({
			tag: 'vaadin-checkbox',
			name: 'pullAllData',
			label: 'Pull historical data for the last 5 years',
			'helper-text': 'Uses the last 5 years from today (API queries are split into windows of at most 366 days).',
			postinit: function(checkbox) {
				pullAllCheckbox = checkbox;
			},
			listeners: {
				change: function(event) {
					updateDateFieldsEnabled(event.target.checked);
				}
			}
		});
	}

	if (includeReplaceExisting) {
		fields.push({
			tag: 'vaadin-checkbox',
			name: 'replaceExistingData',
			label: 'Replace existing GPS, fuel, and trip data',
			'helper-text': 'When checked, rows already imported for the same V6 id in this date window are updated instead of skipped.'
		});
	}

	if (includePrimaryOptions) {
		fields.push(
			{tag: 'vaadin-checkbox', name: 'cleanupInactiveUnits', label: 'Cleanup inactive tractors at finalize'},
			{tag: 'vaadin-integer-field', name: 'inactiveUnitMonths', label: 'Inactive unit months', value: '6'},
			{tag: 'vaadin-integer-field', name: 'reportUnitsLookbackDays', label: 'Report units lookback days', value: '30'},
			{
				tag: 'vaadin-checkbox',
				name: 'allowArchiveOnNextRun',
				label: 'Allow archive on next scheduled run',
				checked: true
			}
		);
	}

	fields.push({tag: 'vaadin-text-field', name: 'note', label: 'Note'});

	return {
		fields: fields,
		updateDateFieldsEnabled: updateDateFieldsEnabled,
		getPullAllCheckbox: function() {
			return pullAllCheckbox;
		}
	};
}

export function isMigrationRunInProgress(record) {
	return !!record && (
		record.runStatus === 'WORKING'
		|| record.runStatus === 'PENDING'
		|| record.runStatus === 'PROVISIONING'
	);
}

export function isStuckWorking(record) {
	return record
		&& record.runStatus === 'WORKING'
		&& !!record.lastFailedBatchStep;
}

export function isStuckPending(record) {
	return record && record.runStatus === 'PENDING';
}

export function canRetryMigration(record) {
	if (!record) {
		return false;
	}
	if (record.migrationPhase !== 'INITIAL' && record.migrationPhase !== 'FINALIZATION') {
		return false;
	}
	return record.runStatus === 'ERROR' || isStuckWorking(record) || isStuckPending(record);
}

export function canResyncMigration(record) {
	return !!record
		&& (record.migrationPhase === 'VALIDATION' || record.migrationPhase === 'FINALIZATION')
		&& !isMigrationRunInProgress(record);
}

/**
 * Primary migration options can be changed while the migration is still active
 * (retry / re-sync). Once Complete, options are historical only.
 */
export function canEditMigration(record) {
	return !!record
		&& record.migrationPhase !== 'COMPLETE'
		&& !isMigrationRunInProgress(record);
}

function formatOptionDate(value) {
	if (!value) {
		return 'N/A';
	}
	var date = TimeUtil.parseDate(value);
	return date ? TimeUtil.formatDateStandard(date) : 'N/A';
}

function formatOptionYesNo(value) {
	return value ? 'Yes' : 'No';
}

/**
 * vaadin-date-picker values saved through dl-form-panel use UTC end-of-day; recover the
 * picked calendar day so we can apply company start/end boundaries.
 */
function calendarDayFromDatePickerSave(value) {
	if (!value) {
		return null;
	}
	if (typeof value === 'string') {
		return value.length >= 10 ? value.substring(0, 10) : null;
	}
	var month = String(value.getUTCMonth() + 1).padStart(2, '0');
	var day = String(value.getUTCDate()).padStart(2, '0');
	return value.getUTCFullYear() + '-' + month + '-' + day;
}

/**
 * Read-only option lines for status / history views.
 * Returns [{label, value}, ...] suitable for display.
 */
export function getMigrationOptionSummaryLines(record) {
	if (!record) {
		return [];
	}
	var lines = [
		{label: 'Data Start', value: formatOptionDate(record.dataStartDate)},
		{label: 'Data End', value: formatOptionDate(record.dataEndDate)},
		{label: 'Pull historical data (last 5 years)', value: formatOptionYesNo(record.pullAllData)},
		{label: 'Replace existing GPS, fuel, and trip data', value: formatOptionYesNo(record.replaceExistingData)},
		{label: 'Cleanup inactive tractors', value: formatOptionYesNo(record.cleanupInactiveUnits)},
		{
			label: 'Inactive unit months',
			value: record.inactiveUnitMonths != null ? String(record.inactiveUnitMonths) : 'N/A'
		},
		{
			label: 'Report units lookback days',
			value: record.reportUnitsLookbackDays != null ? String(record.reportUnitsLookbackDays) : 'N/A'
		},
		{label: 'Allow archive on next scheduled run', value: formatOptionYesNo(record.allowArchiveOnNextRun)},
		{label: 'Note', value: record.note || '—'}
	];
	return appendMigrationRunStepSummaryLines(record, lines);
}

export function collectMigrationOptionData(formPanel) {
	var data = formPanel.getData() || {};
	if (Object.prototype.hasOwnProperty.call(data, 'pullAllData')) {
		data.pullAllData = !!data.pullAllData;
	}
	if (Object.prototype.hasOwnProperty.call(data, 'cleanupInactiveUnits')) {
		data.cleanupInactiveUnits = !!data.cleanupInactiveUnits;
	}
	if (Object.prototype.hasOwnProperty.call(data, 'allowArchiveOnNextRun')) {
		data.allowArchiveOnNextRun = !!data.allowArchiveOnNextRun;
	}
	if (Object.prototype.hasOwnProperty.call(data, 'replaceExistingData')) {
		data.replaceExistingData = !!data.replaceExistingData;
	}
	if (data.inactiveUnitMonths !== undefined && data.inactiveUnitMonths !== null && data.inactiveUnitMonths !== '') {
		data.inactiveUnitMonths = parseInt(data.inactiveUnitMonths, 10);
	}
	if (data.reportUnitsLookbackDays !== undefined && data.reportUnitsLookbackDays !== null && data.reportUnitsLookbackDays !== '') {
		data.reportUnitsLookbackDays = parseInt(data.reportUnitsLookbackDays, 10);
	}
	if (data.pullAllData) {
		// Server applies the default 5-year range from today.
		data.dataStartDate = null;
		data.dataEndDate = null;
	} else {
		var startCalendarDay = calendarDayFromDatePickerSave(data.dataStartDate);
		var endCalendarDay = calendarDayFromDatePickerSave(data.dataEndDate);
		if (startCalendarDay) {
			data.dataStartDate = TimeUtil.startOfDayCompanyTime(startCalendarDay);
		}
		if (endCalendarDay) {
			data.dataEndDate = TimeUtil.endOfDayCompanyTime(endCalendarDay);
		}
	}
	return data;
}

/**
 * Payload for a re-sync run: date range / pull history / note only.
 * Primary options are inherited server-side from the primary migration.
 */
export function collectResyncOptionData(formPanel) {
	var data = collectMigrationOptionData(formPanel);
	return {
		dataStartDate: data.dataStartDate,
		dataEndDate: data.dataEndDate,
		pullAllData: !!data.pullAllData,
		replaceExistingData: !!data.replaceExistingData,
		note: data.note || null
	};
}

/**
 * Load option values onto a form. For re-sync, pass {clearNote: true} so the
 * note starts blank (it should describe why this re-sync is being run).
 */
export function loadMigrationOptionsOntoForm(formPanel, record, optionFieldHelper, options) {
	if (!formPanel || !record) {
		return;
	}
	options = options || {};
	var loadModel = Object.assign({}, record);
	loadModel.inactiveUnitMonths = record.inactiveUnitMonths != null
		? String(record.inactiveUnitMonths)
		: '6';
	loadModel.reportUnitsLookbackDays = record.reportUnitsLookbackDays != null
		? String(record.reportUnitsLookbackDays)
		: '30';
	if (options.clearNote) {
		loadModel.note = '';
	}
	formPanel.load(loadModel);
	if (optionFieldHelper) {
		optionFieldHelper.updateDateFieldsEnabled(!!record.pullAllData);
	}
}

export function makeDialogButtonBar(buttons) {
	return ElementUtil.create({
		tag: 'div',
		style: 'text-align: center; margin-top: 16px;',
		defaults: {tag: 'vaadin-button'},
		children: buttons
	});
}
